/** Regras de códigos por tipo de usuário Go. */
export const GO_TIPO = {
  TECNICO: 1,
  ENTREGADOR: 2,
  AMBOS: 3,
};

export const CODE_MAX_LEN = 4;

/** Técnico nativo (cliente com cutover): G + 3 dígitos; os demais códigos são só dígitos do ERP. */
export function sanitizeGoCode(raw) {
  const s = String(raw ?? '').trim().toUpperCase();
  if (/^G\d*$/.test(s)) return s.slice(0, CODE_MAX_LEN);
  return s.replace(/\D/g, '').slice(0, CODE_MAX_LEN);
}

export function isValidGoCode(raw) {
  return sanitizeGoCode(raw).length === CODE_MAX_LEN;
}

export function goCodeKey(raw) {
  const s = sanitizeGoCode(raw);
  if (!s || s.startsWith('G')) return s;
  return s.padStart(CODE_MAX_LEN, '0');
}

export function goCodeInUseMessage(field, code, ownerName) {
  const label = field === 'codmoto' ? 'motorista' : 'técnico';
  const shown = goCodeKey(code) || String(code || '').trim();
  const name = String(ownerName || '').trim() || 'outro usuário';
  return `O código de ${label} ${shown} já está vinculado a ${name}.`;
}

export function findGoCodeOwner(rows, field, code, excludeIdUser) {
  const key = goCodeKey(code);
  if (!key) return null;
  const prop = field === 'codmoto' ? 'codmoto' : 'codtec';
  return (
    (rows || []).find((r) => {
      if (r.enabled === false) return false;
      if (Number(r.idUser) === Number(excludeIdUser)) return false;
      return goCodeKey(r[prop]) === key;
    }) || null
  );
}

export function buildGoCodeUsage(rows) {
  const usedTech = {};
  const usedMoto = {};
  for (const r of rows || []) {
    if (r.enabled === false) continue;
    const tech = goCodeKey(r.codtec);
    if (tech) usedTech[tech] = { idUser: r.idUser, username: r.username || r.email || '' };
    const moto = goCodeKey(r.codmoto);
    if (moto) usedMoto[moto] = { idUser: r.idUser, username: r.username || r.email || '' };
  }
  return { usedTech, usedMoto };
}

export function filterAvailableGoCodes(catalog, usedMap, excludeIdUser) {
  return (catalog || []).filter((item) => {
    const owner = usedMap?.[item.code];
    if (!owner) return true;
    return Number(owner.idUser) === Number(excludeIdUser);
  });
}

export function goCodeConflictMessage(rows, idUser, patch) {
  if (!patch) return null;
  if (Object.prototype.hasOwnProperty.call(patch, 'codtech') && patch.codtech) {
    const owner = findGoCodeOwner(rows, 'codtech', patch.codtech, idUser);
    if (owner) return goCodeInUseMessage('codtech', patch.codtech, owner.username);
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'codmoto') && patch.codmoto) {
    const owner = findGoCodeOwner(rows, 'codmoto', patch.codmoto, idUser);
    if (owner) return goCodeInUseMessage('codmoto', patch.codmoto, owner.username);
  }
  return null;
}

export function isGoCodeTaken(usedMap, code, excludeIdUser) {
  const key = goCodeKey(code);
  const owner = key ? usedMap?.[key] : null;
  return Boolean(owner && Number(owner.idUser) !== Number(excludeIdUser));
}

/** Quais códigos o tipo exige / permite. */
export function codesPolicyForTipo(tipo) {
  const t = Number(tipo);
  if (t === GO_TIPO.TECNICO) {
    return { needCodtech: true, needCodmoto: false, allowCodtech: true, allowCodmoto: false };
  }
  if (t === GO_TIPO.ENTREGADOR) {
    return { needCodtech: false, needCodmoto: true, allowCodtech: false, allowCodmoto: true };
  }
  if (t === GO_TIPO.AMBOS) {
    return { needCodtech: true, needCodmoto: true, allowCodtech: true, allowCodmoto: true };
  }
  return { needCodtech: false, needCodmoto: false, allowCodtech: false, allowCodmoto: false };
}

/**
 * Monta patch de códigos ao mudar tipo: limpa incompatíveis e valida obrigatórios.
 * @returns {{ ok: true, patch: object } | { ok: false, message: string, missing: string[] }}
 */
export function buildCodesPatchForTipo(tipo, { codtec = '', codmoto = '', codtechInput, codmotoInput } = {}) {
  const policy = codesPolicyForTipo(tipo);
  const nextTech = policy.allowCodtech
    ? sanitizeGoCode(codtechInput != null ? codtechInput : codtec)
    : '';
  const nextMoto = policy.allowCodmoto
    ? sanitizeGoCode(codmotoInput != null ? codmotoInput : codmoto)
    : '';

  const missing = [];
  if (policy.needCodtech && nextTech.length !== CODE_MAX_LEN) missing.push('codtech');
  if (policy.needCodmoto && nextMoto.length !== CODE_MAX_LEN) missing.push('codmoto');

  if (missing.length) {
    const labels = missing.map((m) => (m === 'codtech' ? 'código do técnico' : 'código do motorista'));
    return {
      ok: false,
      missing,
      message: `Informe ${labels.join(' e ')} com 4 dígitos.`,
      nextTech,
      nextMoto,
    };
  }

  return {
    ok: true,
    patch: {
      codtech: policy.allowCodtech ? nextTech : '',
      codmoto: policy.allowCodmoto ? nextMoto : '',
    },
    nextTech,
    nextMoto,
  };
}
